/**
 * VesselTimelineDataProvider - Handles vessel timeline data requests from popups
 *
 * This component runs in the main window and listens for POPUP_INIT requests
 * for 'vessel-timeline' type. It fetches vessel data and responds to the popup.
 * Also handles REQUEST_DATA messages for ship track updates when date range changes.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  useGetShipByImo,
  useShipPortCallsHistory,
  useShipTrackFetch,
} from '@ais-ui/api';
import {
  usePopupMessages,
  MainWindowActions,
  MessageType,
  PopupMessage,
  PopupInitPayload,
  DataType,
} from './PopupEvents';
import { PopupType } from '@ais-ui/popups';
import { usePermissions } from '../Permissions/PermissionsProvider';

/** Serialized ShipInfo passed from main window when opening the popup */
interface SerializedShipInfo {
  name?: string;
  imo?: string;
  mmsi?: string;
  callSign?: string;
  country?: string;
  aisType?: string;
  sourceType?: string;
  shipType?: string;
  width?: string;
  length?: string;
  draught?: string;
  heading?: string;
  rateOfTurn?: string;
  courseOverGround?: string;
  speedOverGround?: string;
  destination?: string;
  navigationalStatus?: string;
  eta?: string;
  portEta?: string;
  lastAisLatitude?: string;
  lastAisLongitude?: string;
  lastAisReport?: string;
  lastAisSatLatitude?: string;
  lastAisSatLongitude?: string;
  lastAisSatReport?: string;
}

/** Vessel details parsed from URL params */
type VesselDetailsFromParams = SerializedShipInfo;

interface ActiveRequest {
  popupId: string;
  imo: number;
  mmsi?: number;
  name?: string;
  dateRange?: { from?: Date; to?: Date };
  chartDateRange?: { from?: Date; to?: Date };
  vesselDetails?: VesselDetailsFromParams;
}

interface ShipTrackRequest {
  popupId: string;
  requestId: string;
  imo: number;
  chartDateRange: { from: Date; to: Date };
}

export const VesselTimelineDataProvider = (): null => {
  const [activeRequest, setActiveRequest] = useState<ActiveRequest | null>(
    null,
  );
  const [shipTrackRequest, setShipTrackRequest] =
    useState<ShipTrackRequest | null>(null);
  // Track which request ID we've already responded to, to avoid sending stale data
  const respondedRequestIdRef = useRef<string | null>(null);
  // Track if we've seen the loading cycle start for ship track requests
  const hasSeenLoadingStartRef = useRef(false);
  const permissions = usePermissions();

  // Memoize ship data to prevent creating new objects on every render
  const shipData = useMemo(() => {
    return activeRequest?.imo ? { imo: activeRequest.imo } : undefined;
  }, [activeRequest?.imo]);

  // Memoize date range to ensure stable reference
  const dateRange = useMemo(() => {
    return activeRequest?.dateRange;
  }, [activeRequest?.dateRange]);

  // Fetch port call history
  const {
    data: portCallsData,
    isLoading: isLoadingPortCalls,
    isError: isErrorPortCalls,
  } = useShipPortCallsHistory(shipData, dateRange);

  // Fetch extended vessel info (inspections, bans, sanctions, register, advanced vessel)
  const hasShipDbPermissions =
    (permissions.mdmIntegrity ||
      permissions.mdmAdvancedVesselInfo ||
      permissions.mdmRegister) &&
    !!activeRequest?.imo;

  const {
    data: extendedVesselInfo,
    isLoading: isLoadingExtendedInfo,
    isError: isErrorExtendedInfo,
  } = useGetShipByImo(activeRequest?.imo || 0, hasShipDbPermissions);

  // Ship track dates - prioritize shipTrackRequest for date range updates,
  // otherwise use activeRequest.chartDateRange for initial load, or default to last 24 hours
  const { shipTrackBeginDate, shipTrackEndDate, shipTrackImo } = useMemo(() => {
    // Priority 1: Ship track request (for date range updates)
    if (shipTrackRequest) {
      return {
        shipTrackBeginDate: shipTrackRequest.chartDateRange.from,
        shipTrackEndDate: shipTrackRequest.chartDateRange.to,
        shipTrackImo: shipTrackRequest.imo,
      };
    }

    // Priority 2: Active request with chart date range (for initial load)
    if (activeRequest?.imo) {
      if (
        activeRequest.chartDateRange?.from &&
        activeRequest.chartDateRange?.to
      ) {
        return {
          shipTrackBeginDate: activeRequest.chartDateRange.from,
          shipTrackEndDate: activeRequest.chartDateRange.to,
          shipTrackImo: activeRequest.imo,
        };
      }

      // Default to last 24 hours
      const end = new Date();
      const begin = new Date();
      begin.setHours(begin.getHours() - 24);
      return {
        shipTrackBeginDate: begin,
        shipTrackEndDate: end,
        shipTrackImo: activeRequest.imo,
      };
    }

    return {
      shipTrackBeginDate: undefined,
      shipTrackEndDate: undefined,
      shipTrackImo: undefined,
    };
  }, [activeRequest?.imo, activeRequest?.chartDateRange, shipTrackRequest]);

  // Ship data for track fetch - use shipTrackImo from computed dates
  const shipTrackShipData = useMemo(() => {
    return shipTrackImo ? { imo: shipTrackImo } : undefined;
  }, [shipTrackImo]);

  // Fetch ship track - only when we have valid dates
  const {
    data: shipTrackData,
    isLoading: isLoadingShipTrack,
    isError: isErrorShipTrack,
  } = useShipTrackFetch({
    shipData: shipTrackShipData,
    beginDate: shipTrackBeginDate,
    endDate: shipTrackEndDate,
    resolution: '00:10:00',
  });

  // Listen for popup init requests and data requests
  usePopupMessages(
    useCallback((msg: PopupMessage) => {
      // Handle initial data request
      if (msg.type === MessageType.POPUP_INIT) {
        const payload = msg.payload as PopupInitPayload;
        if (payload.popupType !== PopupType.VesselTimeline) return;

        const { popupId, params } = payload;
        const imo = params?.imo ? Number(params.imo) : 0;
        const mmsi = params?.mmsi ? Number(params.mmsi) : undefined;
        const name = params?.name as string | undefined;

        if (!imo) {
          MainWindowActions.sendInitResponse(popupId, {
            error: 'No IMO number provided',
          });
          return;
        }

        // Parse date range from params
        const dateRange = {
          from: params?.dateFrom
            ? new Date(params.dateFrom as string)
            : undefined,
          to: params?.dateTo ? new Date(params.dateTo as string) : undefined,
        };

        // Parse chart date range from params (for ship track)
        const chartDateRange = {
          from: params?.chartDateFrom
            ? new Date(params.chartDateFrom as string)
            : undefined,
          to: params?.chartDateTo
            ? new Date(params.chartDateTo as string)
            : undefined,
        };

        // Parse vessel details from shipInfo param if provided
        let vesselDetails: VesselDetailsFromParams | undefined;
        if (params?.shipInfo) {
          try {
            vesselDetails = JSON.parse(params.shipInfo as string);
          } catch (e) {
            console.warn(
              '[VesselTimelineDataProvider] Failed to parse shipInfo:',
              e,
            );
          }
        }

        // Set active request to trigger data fetching
        setActiveRequest({
          popupId,
          imo,
          mmsi,
          name,
          dateRange,
          chartDateRange,
          vesselDetails,
        });
        return;
      }

      // Handle ship track data request (for chart date range changes)
      if (msg.type === MessageType.REQUEST_DATA) {
        const payload = msg.payload as {
          dataType: string;
          params?: Record<string, unknown>;
        };
        if (payload.dataType !== DataType.SHIP_TRACK) return;
        if (!msg.popupId) return;

        const params = payload.params;
        const imo = params?.imo ? Number(params.imo) : 0;
        const chartDateFrom = params?.chartDateFrom
          ? new Date(params.chartDateFrom as string)
          : null;
        const chartDateTo = params?.chartDateTo
          ? new Date(params.chartDateTo as string)
          : null;

        if (!imo || !chartDateFrom || !chartDateTo) {
          MainWindowActions.sendDataResponse(msg.popupId, msg.id, {
            error:
              'Missing required parameters (imo, chartDateFrom, chartDateTo)',
          });
          return;
        }

        // Set ship track request to trigger fetch
        setShipTrackRequest({
          popupId: msg.popupId,
          requestId: msg.id,
          imo,
          chartDateRange: { from: chartDateFrom, to: chartDateTo },
        });
      }
    }, []),
    [],
  );

  // When data is ready, send response to popup
  useEffect(() => {
    if (!activeRequest) return;

    // Wait for port calls (main data) to complete loading
    // Ship track is optional - don't block on it if it errors out
    const isMainDataLoading =
      isLoadingPortCalls || (permissions.mdmIntegrity && isLoadingExtendedInfo);

    // Ship track can still be loading, but if it errors, proceed anyway
    const shipTrackReady = !isLoadingShipTrack || isErrorShipTrack;

    if (isMainDataLoading || !shipTrackReady) return;

    const { popupId, imo, mmsi, name, dateRange, vesselDetails } =
      activeRequest;

    // Check for critical errors (port calls is main data)
    if (isErrorPortCalls) {
      MainWindowActions.sendInitResponse(popupId, {
        vessel: { imo, mmsi, name },
        portCalls: [],
        error: 'Failed to fetch port calls data',
        permissions: {
          mdmIntegrity: permissions.mdmIntegrity,
        },
      });
      setActiveRequest(null);
      return;
    }

    // Convert port calls to serializable format
    const portCalls = (portCallsData?.records || []).map((record) => ({
      ata: record.ata?.toISOString(),
      atd: record.atd?.toISOString(),
      port: record.port
        ? {
            name: record.port.name,
            locode: record.port.locode,
          }
        : undefined,
    }));

    // Convert ship track to serializable format (only if not errored and has data)
    let shipTrack = null;
    if (
      !isErrorShipTrack &&
      shipTrackData?.track?.coordinates &&
      shipTrackData.track.coordinates.length > 0
    ) {
      shipTrack = {
        coordinates: shipTrackData.track.coordinates,
      };
    }

    // Generate dataKey based on actual data content to prevent unnecessary re-renders
    const portCallsKey =
      portCalls.length > 0
        ? `${portCalls.length}_${portCalls[0]?.ata || ''}_${portCalls[portCalls.length - 1]?.atd || ''}`
        : '0';
    const shipTrackKey = shipTrack?.coordinates?.length
      ? `${shipTrack.coordinates.length}_${shipTrack.coordinates[0]?.[0] || 0}`
      : '0';
    const dataKey = `${imo}_${portCallsKey}_${shipTrackKey}`;

    // Generate separate chartDataKey that includes date range for chart-specific updates
    const chartDateKey =
      activeRequest.chartDateRange?.from && activeRequest.chartDateRange?.to
        ? `${activeRequest.chartDateRange.from.getTime()}_${activeRequest.chartDateRange.to.getTime()}`
        : 'default';
    const chartDataKey = `${imo}_${shipTrackKey}_${chartDateKey}`;

    // Build last port call data from shipdb if available
    let lastPortCallData = null;
    if (extendedVesselInfo?.basic?.portCalls?.length) {
      const lastPortCallFromDb =
        extendedVesselInfo.basic.portCalls[
          extendedVesselInfo.basic.portCalls.length - 1
        ];
      lastPortCallData = {
        portName: lastPortCallFromDb.port_name,
        locode: lastPortCallFromDb.un_location_code,
        country: lastPortCallFromDb.country,
        arrivalTime: lastPortCallFromDb.ata,
        departureTime: lastPortCallFromDb.atd,
        latitude: lastPortCallFromDb.latitude,
        longitude: lastPortCallFromDb.longitude,
      };
    }

    // Send response
    MainWindowActions.sendInitResponse(popupId, {
      vessel: { imo, mmsi, name },
      shipInfo: vesselDetails,
      lastPortCallData,
      portCalls,
      advancedIntegrity: extendedVesselInfo?.advancedIntegrity,
      advancedVessel: extendedVesselInfo?.advancedVessel,
      advancedRegister: extendedVesselInfo?.advancedRegister,
      shipTrack,
      dateRange: {
        from: dateRange?.from?.toISOString() || '',
        to: dateRange?.to?.toISOString() || '',
      },
      permissions: {
        mdmIntegrity: permissions.mdmIntegrity,
        mdmAdvancedVesselInfo: permissions.mdmAdvancedVesselInfo,
        mdmRegister: permissions.mdmRegister,
      },
      dataKey,
      chartDataKey,
    });

    // Clear active request
    setActiveRequest(null);
  }, [
    activeRequest,
    isLoadingPortCalls,
    isLoadingExtendedInfo,
    isLoadingShipTrack,
    isErrorPortCalls,
    isErrorExtendedInfo,
    isErrorShipTrack,
    portCallsData,
    extendedVesselInfo,
    shipTrackData,
    permissions.mdmIntegrity,
    permissions.mdmAdvancedVesselInfo,
    permissions.mdmRegister,
  ]);

  // Handle ship track request response (for date range changes)

  // Reset the loading flag when a new request comes in
  useEffect(() => {
    if (shipTrackRequest) {
      hasSeenLoadingStartRef.current = false;
    }
  }, [shipTrackRequest]);

  // Track when loading starts, or handle immediately available cached data
  useEffect(() => {
    if (!shipTrackRequest) return;

    if (isLoadingShipTrack) {
      // Loading started - mark it
      hasSeenLoadingStartRef.current = true;
    } else if (!hasSeenLoadingStartRef.current && shipTrackData) {
      // Data is immediately available (cached) without loading cycle
      hasSeenLoadingStartRef.current = true;
    }
  }, [shipTrackRequest, isLoadingShipTrack, shipTrackData]);

  // Send response only after loading cycle completes
  useEffect(() => {
    if (!shipTrackRequest) return;

    // Don't respond until we've seen loading start and complete (or cached data)
    // This prevents sending stale data from a previous request
    if (!hasSeenLoadingStartRef.current) return;
    if (isLoadingShipTrack) return;

    // Prevent duplicate responses for the same request
    if (respondedRequestIdRef.current === shipTrackRequest.requestId) return;

    const { popupId, requestId, imo, chartDateRange } = shipTrackRequest;

    // Convert ship track to serializable format
    let shipTrack = null;
    if (
      !isErrorShipTrack &&
      shipTrackData?.track?.coordinates &&
      shipTrackData.track.coordinates.length > 0
    ) {
      shipTrack = {
        coordinates: shipTrackData.track.coordinates,
      };
    }

    // Generate chartDataKey for this response
    const shipTrackKey = shipTrack?.coordinates?.length
      ? `${shipTrack.coordinates.length}_${shipTrack.coordinates[0]?.[0] || 0}`
      : '0';
    const chartDateKey = `${chartDateRange.from.getTime()}_${chartDateRange.to.getTime()}`;
    const chartDataKey = `${imo}_${shipTrackKey}_${chartDateKey}`;

    // Mark this request as responded
    respondedRequestIdRef.current = requestId;

    // Send response
    MainWindowActions.sendDataResponse(popupId, requestId, {
      shipTrack,
      chartDataKey,
      error: isErrorShipTrack ? 'Failed to fetch ship track data' : undefined,
    });

    // Clear ship track request
    setShipTrackRequest(null);
  }, [
    shipTrackRequest,
    isLoadingShipTrack,
    isErrorShipTrack,
    shipTrackData,
    permissions.mdmAdvancedVesselInfo,
    permissions.mdmRegister,
  ]);

  return null;
};
